import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Badge,
    Box,
    Flex,
    HStack,
    Icon,
    Link,
    Text,
    VStack,
} from '@chakra-ui/react';
import {
    TbUpload,
    TbFlag,
    TbEdit,
    TbArchive,
    TbArchiveOff,
    TbSettings,
    TbRefresh,
    TbAlertTriangle,
} from 'react-icons/tb';
import { FiCheckCircle, FiFileText } from 'react-icons/fi';
import React from 'react';
import {
    EnrichedInvoiceActivityLogEntry,
    InvoiceActivityLogChange,
    parseInvoiceActivityChanges,
} from '../../hooks/invoice/useGetInvoiceActivityLogs';
import {
    INVOICE_REASON_FLAG_CONFIG,
    NUMERIC_REASON_BADGE_SCHEME,
    type InvoiceReasonFlagKey,
} from './invoiceReasonFlagConfig';
import { InvoiceReasonFlagBadge } from './InvoiceReasonFlagBadge';
import { brandCopy } from '../../constants/brand';

export const EVENT_TYPE_LABELS: Record<string, string> = {
    'invoice-uploaded': 'Invoice Uploaded',
    'invoice-processed': 'Invoice Processed',
    'invoice-flagged': 'Invoice Flagged',
    'invoice-modified': 'Invoice Modified',
    'invoice-submitted': 'Invoice Submitted',
    'invoice-submit-failed': 'Submit Failed',
    'invoice-archived': 'Invoice Archived',
    'invoice-unarchived': 'Invoice Unarchived',
    'invoices-bulk-archived': 'Bulk Archive',
    'invoices-bulk-unarchived': 'Bulk Unarchive',
    'invoice-pages-replaced': 'Pages Replaced',
    'invoice-settings-changed': 'Settings Changed',
};

export const EVENT_TYPE_COLORS: Record<string, string> = {
    'invoice-uploaded': '#3182CE',
    'invoice-processed': '#0071E3',
    'invoice-flagged': '#DD6B20',
    'invoice-modified': '#0071E3',
    'invoice-submitted': '#38A169',
    'invoice-submit-failed': '#C53030',
    'invoice-archived': '#718096',
    'invoice-unarchived': '#3182CE',
    'invoices-bulk-archived': '#718096',
    'invoices-bulk-unarchived': '#3182CE',
    'invoice-pages-replaced': '#0987A0',
    'invoice-settings-changed': '#805AD5',
};

export const EVENT_TYPE_ICON: Record<string, React.ElementType> = {
    'invoice-uploaded': TbUpload as React.ElementType,
    'invoice-processed': FiFileText as React.ElementType,
    'invoice-flagged': TbFlag as React.ElementType,
    'invoice-modified': TbEdit as React.ElementType,
    'invoice-submitted': FiCheckCircle as React.ElementType,
    'invoice-submit-failed': TbAlertTriangle as React.ElementType,
    'invoice-archived': TbArchive as React.ElementType,
    'invoice-unarchived': TbArchiveOff as React.ElementType,
    'invoices-bulk-archived': TbArchive as React.ElementType,
    'invoices-bulk-unarchived': TbArchiveOff as React.ElementType,
    'invoice-pages-replaced': TbRefresh as React.ElementType,
    'invoice-settings-changed': TbSettings as React.ElementType,
};

const FIELD_LABELS: Record<string, string> = {
    'invoice-number': 'Invoice #',
    'ro-number': 'RO #',
    'purchase-order-number': 'PO #',
    'supplier-text': 'Vendor',
    'invoice-type': 'Invoice Type',
    'location-id': 'Location',
    'pay-settlement-type': 'Pay Settlement',
    'order-total': 'Order Total',
    'subtotal': 'Subtotal',
    'freight-total': 'Freight Total',
    'taxes-total': 'Taxes Total',
    'fuel-total': 'Fuel Total',
    'restocking-fee': 'Restocking Fee',
    'discount-total': 'Discount Total',
    lineItems: 'Line Items',
    freightItems: 'Freight Items',
    fuelItems: 'Fuel Items',
    restockingFeeItems: 'Misc Fees',
    discountItems: 'Discount Items',
    ignoredParts: 'Ignored Parts',
    'manual-matches': 'Manual Matches',
    'unmatched-part-indices': 'Unmatched Parts',
    'line-item-correction-metadata': 'Line Corrections',
    'line-item-correction-reverted': 'Reverted Correction',
    'reconciliation-refreshed': 'Reconciliation Refreshed',
};

const INVOICE_TYPE_LABELS: Record<string, string> = {
    received: 'Received',
    returned: 'Returned',
    return_slip: 'Return Slip',
    sublet: 'Sublet',
};

// Matches the Invoice Type column colors in the parts invoicing table.
const INVOICE_TYPE_COLOR_SCHEMES: Record<string, string> = {
    received: 'blue',
    returned: 'red',
    return_slip: 'yellow',
    sublet: 'green',
};

// Stored activity-log reason codes → the same Reason badges used on the
// invoices list and invoice header (`INVOICE_REASON_FLAG_CONFIG`).
const FLAG_REASON_TO_CONFIG_KEY: Record<string, InvoiceReasonFlagKey> = {
    missing_ro: 'ro',
    missing_in_nex: 'nex',
    vendor_mismatch: 'vendor',
    special_vendor: 'specialVendor',
    uploader_location_mismatch: 'locationMismatch',
    stale_nex_folder: 'stale',
    price_mismatch: 'price',
    discount_mismatch: 'discount',
    qty_mismatch: 'qty',
    total_mismatch: 'total',
    math_mismatch: 'math',
    duplicate_submission: 'dupe',
    // Invoice-detail header treats missing extracted lines as the MATH flag.
    no_extracted_line_items: 'math',
};

const EXTRA_FLAG_REASON_BADGES: Record<string, { label: string; colorScheme: string }> = {
    nex_submission_error: { label: 'NEX Error', colorScheme: NUMERIC_REASON_BADGE_SCHEME },
    restocking_fee_on_return: { label: 'Restocking on Return', colorScheme: NUMERIC_REASON_BADGE_SCHEME },
};

function getActivityLogFlagBadge(reason: string): { label: string; colorScheme: string } {
    const configKey = FLAG_REASON_TO_CONFIG_KEY[reason];
    if (configKey) {
        const config = INVOICE_REASON_FLAG_CONFIG[configKey];
        return { label: config.label, colorScheme: config.colorScheme };
    }
    return EXTRA_FLAG_REASON_BADGES[reason] ?? { label: reason, colorScheme: NUMERIC_REASON_BADGE_SCHEME };
}

function uniqueActivityLogFlagBadges(
    reasons: string[],
): Array<{ reason: string; label: string; colorScheme: string }> {
    const seen = new Set<string>();
    const badges: Array<{ reason: string; label: string; colorScheme: string }> = [];
    for (const reason of reasons) {
        const badge = getActivityLogFlagBadge(reason);
        const dedupeKey = `${badge.label}|${badge.colorScheme}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        badges.push({ reason, ...badge });
    }
    return badges;
}

export function formatDateGroup(ts: string): string {
    const d = new Date(ts);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const logDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((today.getTime() - logDay.getTime()) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

export function formatTime(ts: string): string {
    const d = new Date(ts);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const time = d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
    return `${month}/${day} • ${time}`;
}

export function getActorName(log: EnrichedInvoiceActivityLogEntry): string {
    if (log.userName) {
        return `${log.userName.firstName} ${log.userName.lastName}`.trim();
    }
    if (log.eventType === 'invoice-processed' || log.eventType === 'invoice-flagged') {
        return brandCopy('PartBay');
    }
    if (log.eventType === 'invoice-uploaded' && log.metadata?.source === 'email') {
        return 'Email Inbox';
    }
    if (log.eventType === 'invoice-uploaded' && log.metadata?.source === 'partner') {
        return log.metadata.partner === 'nex' ? 'Nexsyis' : 'Partner';
    }
    return 'System';
}

function formatUploadVia(metadata: Record<string, string>): string {
    if (metadata.source !== 'partner') return metadata.source;
    if (metadata.partner === 'nex') return 'Nexsyis';
    return metadata.partner ? `partner (${metadata.partner})` : 'partner';
}

export function getActionLabel(log: EnrichedInvoiceActivityLogEntry): string {
    return EVENT_TYPE_LABELS[log.eventType] ?? log.eventType;
}

function formatFieldLabel(field: string): string {
    if (FIELD_LABELS[field]) return FIELD_LABELS[field];
    if (field.startsWith('invoicing-settings.')) {
        return field.replace('invoicing-settings.', '').replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
    }
    return field;
}

const CORRECTION_TYPE_LABELS: Record<string, string> = {
    expanded_to_match_nex: 'Expanded to match NEX',
    consolidated_to_match_nex: 'Consolidated to match NEX',
    normalized_to_match_nex: 'Normalized to match NEX',
    part_number_auto_corrected: 'Part # auto-corrected',
    supersession_auto_corrected: 'Supersession auto-corrected',
};

const MONEY_FIELDS = new Set([
    'order-total',
    'subtotal',
    'freight-total',
    'taxes-total',
    'fuel-total',
    'restocking-fee',
    'discount-total',
]);

function parseChangeValue(raw: string | undefined): unknown {
    if (raw === undefined) return undefined;
    try {
        return JSON.parse(raw);
    } catch {
        return raw;
    }
}

function isEmptyChangeValue(value: unknown): boolean {
    if (value === undefined || value === null || value === '') return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value as object).length === 0;
    return false;
}

function formatMoney(value: unknown): string | null {
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? `$${num.toFixed(2)}` : null;
}

function humanizeKey(key: string): string {
    return key
        .replace(/[-_]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, (s) => s.toUpperCase());
}

function describeGenericObject(obj: Record<string, unknown>): string {
    const pairs = Object.entries(obj)
        .filter(([, v]) => v !== undefined && v !== null && typeof v !== 'object')
        .slice(0, 6)
        .map(([k, v]) => `${humanizeKey(k)}: ${v}`);
    return pairs.length > 0 ? pairs.join(' · ') : 'details updated';
}

/** Human-readable one-line summary for a single array entry (line item, manual match, etc.). */
function describeChangeItem(field: string, item: unknown): string {
    if (item === null || item === undefined) return '—';
    if (field === 'unmatched-part-indices' && typeof item === 'number') {
        return `Part ${item + 1}`;
    }
    if (typeof item !== 'object') return String(item);
    const obj = item as Record<string, any>;

    if (field === 'manual-matches' && obj.nexLine) {
        const nex = obj.nexLine as Record<string, any>;
        const qty = typeof nex.qty === 'number' && Number.isFinite(nex.qty) ? nex.qty : undefined;
        const cost = formatMoney(nex.unitCost);
        return [
            `Part ${Number(obj.partIndex) + 1} → ${nex.partNumber ?? '?'}`,
            nex.partDesc,
            qty !== undefined ? `qty ${qty}` : null,
            cost ? `@ ${cost}` : null,
        ]
            .filter(Boolean)
            .join(' · ');
    }

    if (field === 'line-item-correction-metadata') {
        const label = CORRECTION_TYPE_LABELS[String(obj.type)] ?? humanizeKey(String(obj.type ?? 'correction'));
        return obj.partNumber ? `${label} · ${obj.partNumber}` : label;
    }

    // Invoice line-item-like objects (lineItems, freight/fuel items, misc fees, discounts, ignored parts).
    if (obj.partNumber !== undefined || obj.title !== undefined) {
        const unit = formatMoney(obj.unitBuyPrice);
        const total = formatMoney(obj.totalBuyPrice);
        return [
            [obj.partNumber, obj.title].filter(Boolean).join(' — '),
            obj.qty !== undefined ? `qty ${obj.qty}` : null,
            unit ? `@ ${unit}` : null,
            total ? `= ${total}` : null,
        ]
            .filter(Boolean)
            .join(' · ');
    }

    return describeGenericObject(obj);
}

function renderArrayChange(field: string, before: unknown[], after: unknown[]) {
    const beforeKeys = before.map((item) => JSON.stringify(item));
    const afterKeys = after.map((item) => JSON.stringify(item));
    const beforeSet = new Set(beforeKeys);
    const afterSet = new Set(afterKeys);
    const added = after.filter((_, i) => !beforeSet.has(afterKeys[i]));
    const removed = before.filter((_, i) => !afterSet.has(beforeKeys[i]));

    if (added.length === 0 && removed.length === 0) {
        return (
            <Text color="gray.600">
                Reordered ({after.length} item{after.length === 1 ? '' : 's'})
            </Text>
        );
    }

    return (
        <VStack align="stretch" spacing={0.5}>
            {removed.map((item, i) => (
                <Text key={`removed-${i}`} color="red.600" noOfLines={2} wordBreak="break-word">
                    − {describeChangeItem(field, item)}
                </Text>
            ))}
            {added.map((item, i) => (
                <Text key={`added-${i}`} color="green.700" noOfLines={2} wordBreak="break-word">
                    + {describeChangeItem(field, item)}
                </Text>
            ))}
        </VStack>
    );
}

function formatScalarValue(field: string, value: unknown): string {
    if (value === undefined || value === null || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (MONEY_FIELDS.has(field)) {
        return formatMoney(value) ?? String(value);
    }
    return String(value);
}

function renderChangeDetail(change: InvoiceActivityLogChange) {
    const { field } = change;

    if (field === 'line-item-correction-reverted') {
        return <Text color="gray.600">Reverted correction for {change.after}</Text>;
    }
    if (field === 'reconciliation-refreshed') {
        return <Text color="gray.600">Nexsyis reconciliation data refreshed</Text>;
    }

    const before = parseChangeValue(change.before);
    const after = parseChangeValue(change.after);

    if (Array.isArray(before) || Array.isArray(after)) {
        return renderArrayChange(
            field,
            Array.isArray(before) ? before : [],
            Array.isArray(after) ? after : [],
        );
    }

    const beforeIsObject = typeof before === 'object' && before !== null;
    const afterIsObject = typeof after === 'object' && after !== null;
    if (beforeIsObject || afterIsObject) {
        return (
            <VStack align="stretch" spacing={0.5}>
                {beforeIsObject && !isEmptyChangeValue(before) && (
                    <Text color="red.600" noOfLines={2} wordBreak="break-word">
                        − {describeGenericObject(before as Record<string, unknown>)}
                    </Text>
                )}
                {afterIsObject && !isEmptyChangeValue(after) && (
                    <Text color="green.700" noOfLines={2} wordBreak="break-word">
                        + {describeGenericObject(after as Record<string, unknown>)}
                    </Text>
                )}
            </VStack>
        );
    }

    const beforeText = formatScalarValue(field, before);
    const afterText = formatScalarValue(field, after);
    if (isEmptyChangeValue(before)) {
        return <Text color="gray.600">Set to <Text as="span" color="gray.800" fontWeight="medium">{afterText}</Text></Text>;
    }
    if (isEmptyChangeValue(after)) {
        return <Text color="gray.600">Removed (was {beforeText})</Text>;
    }
    return (
        <Text color="gray.600">
            <Text as="span" color="gray.500" textDecoration="line-through">
                {beforeText}
            </Text>
            {' → '}
            <Text as="span" color="gray.800" fontWeight="medium">
                {afterText}
            </Text>
        </Text>
    );
}

/** Drops noise like `null → []` where nothing meaningful changed. */
function isMeaningfulChange(change: InvoiceActivityLogChange): boolean {
    if (change.field === 'line-item-correction-reverted' || change.field === 'reconciliation-refreshed') {
        return true;
    }
    const before = parseChangeValue(change.before);
    const after = parseChangeValue(change.after);
    return !(isEmptyChangeValue(before) && isEmptyChangeValue(after));
}

function renderChanges(changes: InvoiceActivityLogChange[]) {
    const visibleChanges = changes.filter(isMeaningfulChange);
    if (visibleChanges.length === 0) return null;

    const fieldSummary = visibleChanges.map((change) => formatFieldLabel(change.field)).join(', ');

    return (
        <Accordion allowToggle mt={2}>
            <AccordionItem border="none">
                <AccordionButton
                    px={2}
                    py={1.5}
                    borderRadius="md"
                    bg="gray.50"
                    _hover={{ bg: 'gray.100' }}
                    _expanded={{ borderBottomRadius: 0 }}
                >
                    <HStack flex={1} spacing={1.5} minW={0} textAlign="left">
                        <Text fontSize="12px" fontWeight="semibold" color="gray.700" flexShrink={0}>
                            {visibleChanges.length} change{visibleChanges.length === 1 ? '' : 's'}
                        </Text>
                        <Text fontSize="12px" color="gray.500" isTruncated>
                            {fieldSummary}
                        </Text>
                    </HStack>
                    <AccordionIcon color="gray.500" />
                </AccordionButton>
                <AccordionPanel px={2} pt={1.5} pb={2} bg="gray.50" borderBottomRadius="md">
                    <VStack align="stretch" spacing={2}>
                        {visibleChanges.map((change, idx) => (
                            <Box key={`${change.field}-${idx}`} fontSize="12px">
                                <Text fontWeight="semibold" color="gray.700">
                                    {formatFieldLabel(change.field)}
                                </Text>
                                {renderChangeDetail(change)}
                            </Box>
                        ))}
                    </VStack>
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    );
}

function getInvoiceRefLabel(log: EnrichedInvoiceActivityLogEntry): string | null {
    const ref = log.invoiceRef;
    if (!ref) return null;
    const parts: string[] = [];
    if (ref.roNumber) parts.push(`RO ${ref.roNumber}`);
    if (ref.invoiceNumber) parts.push(`Invoice ${ref.invoiceNumber}`);
    if (parts.length === 0) parts.push('View invoice');
    return parts.join(' · ');
}

export interface RenderLogCardOptions {
    /** When set, shows invoice identifiers (RO #, invoice #, vendor) as a link to the invoice. */
    showInvoiceRef?: boolean;
    onInvoiceClick?: (invoiceId: string) => void;
    /**
     * Strips the leading "Invoice " from the action label (e.g. "Invoice Uploaded"
     * → "Uploaded"). Used in the all-invoices activity view where every row is an
     * invoice event, so the repeated prefix is just noise.
     */
    stripInvoicePrefix?: boolean;
    /**
     * Renders the card flat (no border/rounded box, just a bottom divider) to
     * match the order `ActivityLogDrawer` timeline styling.
     */
    flat?: boolean;
}

export function renderLogCard(log: EnrichedInvoiceActivityLogEntry, options?: RenderLogCardOptions) {
    const displayType = log.eventType;
    const color = EVENT_TYPE_COLORS[displayType] ?? '#0071E3';
    const changes =
        log.eventType === 'invoice-modified' || log.eventType === 'invoice-settings-changed'
            ? parseInvoiceActivityChanges(log.metadata)
            : [];
    const flagReasons = uniqueActivityLogFlagBadges(
        log.metadata?.flagReasons?.split(', ').filter(Boolean) ?? [],
    );
    const invoiceRefLabel = options?.showInvoiceRef ? getInvoiceRefLabel(log) : null;
    const invoiceType = log.invoiceTypeAtEvent ?? log.metadata?.invoiceType ?? log.invoiceRef?.invoiceType;
    const invoiceTypeLabel = invoiceType ? INVOICE_TYPE_LABELS[invoiceType] ?? invoiceType : null;
    const actionLabel = options?.stripInvoicePrefix
        ? getActionLabel(log).replace(/^Invoice /, '')
        : getActionLabel(log);

    const cardProps = options?.flat
        ? { bg: 'white', py: 3, borderBottom: '1px solid', borderColor: 'gray.100' as const }
        : { borderWidth: '1px', borderColor: 'gray.200' as const, borderRadius: 'lg', bg: 'white', p: 3 };

    return (
        <Box {...cardProps}>
            <Flex align="flex-start" gap={3}>
                <Flex
                    w="32px"
                    h="32px"
                    borderRadius="full"
                    bg="gray.100"
                    align="center"
                    justify="center"
                    flexShrink={0}
                >
                    <Icon as={EVENT_TYPE_ICON[displayType] ?? (FiFileText as React.ElementType)} w={4} h={4} color={color} />
                </Flex>
                <Box flex={1} minW={0}>
                    <Flex align="center" justify="space-between" gap={2}>
                        <HStack spacing={1.5} minW={0}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                {actionLabel}
                            </Text>
                            <Text fontSize="sm" color="gray.400">·</Text>
                            <Text fontSize="sm" color="gray.600" isTruncated>
                                {getActorName(log)}
                            </Text>
                            {invoiceTypeLabel && (
                                <>
                                    <Text fontSize="sm" color="gray.400" flexShrink={0}>·</Text>
                                    <Badge
                                        colorScheme={INVOICE_TYPE_COLOR_SCHEMES[invoiceType ?? ''] ?? 'gray'}
                                        variant="subtle"
                                        fontSize="10px"
                                        flexShrink={0}
                                    >
                                        {invoiceTypeLabel}
                                    </Badge>
                                </>
                            )}
                        </HStack>
                        <Text fontSize="xs" color="gray.500" flexShrink={0}>
                            {formatTime(log.timestamp)}
                        </Text>
                    </Flex>

                    {invoiceRefLabel && log.invoiceRef && (
                        <Box mt={0.5}>
                            <Link
                                fontSize="13px"
                                color="#0071E3"
                                fontWeight="medium"
                                display="inline-block"
                                onClick={(e) => {
                                    e.preventDefault();
                                    options?.onInvoiceClick?.(log.invoiceRef!.invoiceId);
                                }}
                                href={`/invoices/${log.invoiceRef.invoiceId}`}
                            >
                                {invoiceRefLabel}
                            </Link>
                            {log.invoiceRef.supplierText && (
                                <Box
                                    mt={1.5}
                                    bg="gray.50"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                    px={3}
                                    py={2}
                                >
                                    <Flex gap={3} align="center" minW={0}>
                                        <Text
                                            fontSize="11px"
                                            color="gray.500"
                                            fontWeight="semibold"
                                            textTransform="uppercase"
                                            letterSpacing="wide"
                                            flexShrink={0}
                                        >
                                            Vendor
                                        </Text>
                                        <Text fontSize="13px" color="gray.800" isTruncated minW={0}>
                                            {log.invoiceRef.supplierText}
                                        </Text>
                                    </Flex>
                                </Box>
                            )}
                        </Box>
                    )}

                    {log.eventType === 'invoice-uploaded' && log.metadata?.source && (
                        <Text fontSize="13px" color="gray.600" mt={1}>
                            via {formatUploadVia(log.metadata ?? {})}
                            {log.metadata.imageSource === 'camera' || log.metadata.imageSource === 'library'
                                ? ` · ${log.metadata.imageSource}`
                                : ''}
                            {log.metadata.pageCount ? ` · ${log.metadata.pageCount} page(s)` : ''}
                        </Text>
                    )}

                    {log.eventType === 'invoice-processed' && (
                        <Text fontSize="13px" color="gray.600" mt={1}>
                            {log.metadata?.logicalPages ? `${log.metadata.logicalPages} page(s)` : 'Processed'}
                            {log.metadata?.statusAfter ? ` · ${log.metadata.statusAfter}` : ''}
                        </Text>
                    )}

                    {flagReasons.length > 0 && (
                        <HStack mt={2} spacing={1} flexWrap="wrap">
                            {flagReasons.map((badge) => (
                                <InvoiceReasonFlagBadge
                                    key={badge.reason}
                                    label={badge.label}
                                    colorScheme={badge.colorScheme}
                                />
                            ))}
                        </HStack>
                    )}

                    {log.eventType === 'invoice-submitted' && log.metadata?.mode && (
                        <Text fontSize="13px" color="gray.600" mt={1}>
                            {log.metadata.mode === 'auto' ? 'Auto-submitted' : 'Manually submitted'}
                        </Text>
                    )}

                    {log.eventType === 'invoice-submit-failed' && (
                        <Text fontSize="13px" color="red.600" mt={1} noOfLines={4}>
                            {log.metadata?.detail ?? log.metadata?.title ?? 'Submission failed'}
                        </Text>
                    )}

                    {(log.eventType === 'invoices-bulk-archived' || log.eventType === 'invoices-bulk-unarchived') && (
                        <Text fontSize="13px" color="gray.600" mt={1}>
                            {log.metadata?.count ?? '0'} invoice(s)
                        </Text>
                    )}

                    {changes.length > 0 && renderChanges(changes)}
                </Box>
            </Flex>
        </Box>
    );
}

export function groupByDate(
    logs: EnrichedInvoiceActivityLogEntry[],
): Map<string, EnrichedInvoiceActivityLogEntry[]> {
    const groups = new Map<string, EnrichedInvoiceActivityLogEntry[]>();
    for (const log of logs) {
        const key = formatDateGroup(log.timestamp);
        const group = groups.get(key);
        if (group) group.push(log);
        else groups.set(key, [log]);
    }
    return groups;
}
